import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';

// ===============================
// SENTRY — MONITOREO DE ERRORES
// Captura errores, performance y sesiones de usuario en producción.
// DSN configurable por env (VITE_SENTRY_DSN). Si no está, se desactiva.
// ===============================
const SENTRY_DSN =
  import.meta.env.VITE_SENTRY_DSN ||
  'https://eaf43ed7eb5f97e8de212ad586ae81b9@o4511292780183552.ingest.de.sentry.io/4511315876511824';

// -------------------------------------------------------------
// SISTEMA DE CAPTURA Y REGISTRO DE EVENTOS SENTRY / EXCEPCIONES
// -------------------------------------------------------------
const GASTIFY_ERROR_STORAGE_KEY = 'gastify_captured_errors';

function saveCapturedError(errorData) {
  try {
    const existingRaw = localStorage.getItem(GASTIFY_ERROR_STORAGE_KEY);
    const existing = existingRaw ? JSON.parse(existingRaw) : [];
    
    // Evitar duplicados consecutivos idénticos en un corto período
    const isDuplicate = existing.some(
      (e) =>
        e.message === errorData.message &&
        e.stack === errorData.stack &&
        Math.abs(new Date(e.timestamp) - new Date(errorData.timestamp)) < 2000
    );
    
    if (!isDuplicate) {
      existing.unshift(errorData);
      if (existing.length > 100) existing.pop(); // Limitar historial a 100
      localStorage.setItem(GASTIFY_ERROR_STORAGE_KEY, JSON.stringify(existing));
      
      // Emitir evento para actualizar la UI del Dashboard en tiempo real si está abierta
      window.dispatchEvent(new CustomEvent('gastify:new-error-captured', { detail: errorData }));
    }
  } catch (e) {
    console.error('[Sentry Interceptor] Error al escribir en localStorage:', e);
  }
}

async function saveCapturedErrorToSupabase(errorData) {
  try {
    const { supabase } = await import('./supabase');
    if (supabase) {
      const { error } = await supabase.from('sentry_events').insert([{
        event_id: errorData.id,
        timestamp: errorData.timestamp,
        error_name: errorData.name,
        error_message: errorData.message,
        stack_trace: errorData.stack,
        url: errorData.url,
        user_email: errorData.userEmail,
        user_id: errorData.userId,
        severity: errorData.severity,
        metadata: errorData.metadata
      }]);
      if (error) {
        // La tabla puede no existir o haber problemas de RLS. Se ignora ya que localstorage es el principal.
        console.debug('[Sentry Interceptor] Supabase sync omitted:', error.message);
      }
    }
  } catch (e) {
    // Silencioso
  }
}

// Interceptor global para errores ordinarios en dev/prod
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    // Evitamos duplicar si el error proviene de scripts de extensiones u otros ajenos
    if (event.filename && event.filename.includes('chrome-extension')) return;
    
    const error = event.error;
    const userInfo = window.__gastifyCurrentUser || null;
    const errorData = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      name: error?.name || 'RuntimeError',
      message: event.message || error?.message || 'Error en tiempo de ejecución',
      stack: error?.stack || 'No stack trace available',
      url: window.location.href,
      userEmail: userInfo?.email || 'anonimo@gastify.com',
      userId: userInfo?.id || null,
      organizationId: userInfo?.organization_id || null,
      severity: 'error',
      source: 'runtime-interceptor',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        browser: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        environment: import.meta.env.MODE
      }
    };
    
    saveCapturedError(errorData);
    saveCapturedErrorToSupabase(errorData);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const userInfo = window.__gastifyCurrentUser || null;
    const errorData = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      name: 'UnhandledPromiseRejection',
      message: typeof reason === 'string' ? reason : (reason?.message || 'Promesa rechazada no manejada'),
      stack: reason?.stack || 'No stack trace available',
      url: window.location.href,
      userEmail: userInfo?.email || 'anonimo@gastify.com',
      userId: userInfo?.id || null,
      organizationId: userInfo?.organization_id || null,
      severity: 'warning',
      source: 'promise-interceptor',
      metadata: {
        browser: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        environment: import.meta.env.MODE
      }
    };
    
    saveCapturedError(errorData);
    saveCapturedErrorToSupabase(errorData);
  });
}

// Captura manual de errores desde simulador u otras partes
window.__gastifyCaptureManualError = (error, severity = 'error') => {
  const userInfo = window.__gastifyCurrentUser || null;
  const errorData = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    name: error?.name || 'ManualError',
    message: error?.message || String(error) || 'Error manual capturado',
    stack: error?.stack || 'Manual capture — stack trace generated at call:\n' + new Error().stack,
    url: window.location.href,
    userEmail: userInfo?.email || 'anonimo@gastify.com',
    userId: userInfo?.id || null,
    organizationId: userInfo?.organization_id || null,
    severity: severity,
    source: 'manual',
    metadata: {
      browser: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      environment: import.meta.env.MODE
    }
  };
  saveCapturedError(errorData);
  saveCapturedErrorToSupabase(errorData);
};

// Guard anti doble-init: si el módulo se evalúa dos veces (HMR de Vite en dev,
// o import duplicado), Sentry.init vuelve a instanciar replayIntegration y tira
// "Multiple Sentry Session Replay instances are not supported". getClient() ya
// devuelve un cliente activo tras la primera init, así que evitamos la segunda.
if (SENTRY_DSN && !Sentry.getClient()) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    // Monitoreo de performance: 10% en producción, 100% en desarrollo
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Session Replay: 10% de sesiones normales, 100% de sesiones con error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || 'gastify@dev',
    enabled: import.meta.env.PROD,
    // Ignorar errores ruidosos típicos del browser
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event, hint) {
      try {
        const error = hint?.originalException;
        const userInfo = window.__gastifyCurrentUser || null;
        const errorData = {
          id: event.event_id || crypto.randomUUID(),
          timestamp: new Date().toISOString(),
          name: error?.name || event.exception?.values?.[0]?.type || 'Error',
          message: error?.message || event.exception?.values?.[0]?.value || 'Ocurrió un error inesperado',
          stack: error?.stack || 'No stack trace available',
          url: window.location.href,
          userEmail: userInfo?.email || 'anonimo@gastify.com',
          userId: userInfo?.id || null,
          organizationId: userInfo?.organization_id || null,
          severity: event.level || 'error',
          source: 'sentry',
          metadata: {
            browser: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            environment: import.meta.env.MODE
          }
        };
        
        saveCapturedError(errorData);
        saveCapturedErrorToSupabase(errorData);
      } catch (err) {
        console.error('Error logging Sentry event locally in beforeSend', err);
      }
      return event;
    }
  });
}

// Helper para que otras partes del código seteen el contexto del usuario
// (auth, organization). Se llama desde AuthContext / TrialContext si lo querés.
window.__gastifyTagUser = (userInfo) => {
  window.__gastifyCurrentUser = userInfo; // Guardar globalmente
  if (!SENTRY_DSN) return;
  try {
    Sentry.setUser(userInfo ? {
      id: userInfo.id,
      email: userInfo.email,
      organization_id: userInfo.organization_id,
    } : null);
  } catch { /* noop */ }
};

// ===============================
// POLYFILL crypto.randomUUID
// Necesario para Safari / iOS / HTTP por IP
// Evita que se rompa el click del catalogo en POS
// ===============================
if (typeof crypto !== "undefined" && !crypto.randomUUID) {
  crypto.randomUUID = function randomUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };
}

// ===============================
// ESTILOS
// ===============================
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './index.css';

// ===============================
// SERVICE WORKER — AUTO RELOAD
// Cuando hay una nueva versión deployada, el SW se actualiza
// y esta función recarga la página automáticamente.
// Los usuarios siempre verán la última versión sin Ctrl+Shift+R.
// ===============================
import { registerSWAutoReload } from './utils/swUpdate';
registerSWAutoReload();

// ===============================
// APP
// ===============================
import { AuthProvider } from './context/AuthContext';
import { SiteProvider } from './context/SiteContext';
import App from './App';
import { captureAttribution, initMetaPixel } from './utils/metaPixel';

// Origen de la visita (UTM) y Meta Pixel para la campaña de altas.
// Sin ID de píxel configurado, initMetaPixel no hace nada.
captureAttribution();
initMetaPixel();

// ===============================
// RENDER
// ===============================
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <AuthProvider>
      <SiteProvider>
        <BrowserRouter>
          <App />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3500,
              style: {
                borderRadius: '16px',
                background: '#0f172a',
                color: '#f8fafc',
                fontWeight: 700,
              },
            }}
          />
        </BrowserRouter>
      </SiteProvider>
    </AuthProvider>
  </React.StrictMode>
);
